import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import type { ParagraphFixService } from '../paragraph-fix-service';
import type { ParagraphIssueGroup } from '../../types';
import type { FixContentRequest, FixContentResponse } from '../../apis/fix-content-api';
import { FixContentApi } from '../../apis/fix-content-api';
import { StreamStatus } from '@/services/sse-ps-http/enums';
import { CurrentTaskService } from '@/services/current-task/current-task-service';

/**
 * 段落修复服务实现
 */
@injectable()
export class ParagraphFixServiceImpl implements ParagraphFixService {
    private _onContentGeneratingEmitter = new Emitter<{ issueGroup: ParagraphIssueGroup; isGenerating: boolean }>();
    readonly onContentGenerating = this._onContentGeneratingEmitter.event;

    private _onContentGenerateStartEmitter = new Emitter<{ issueGroup: ParagraphIssueGroup }>();
    readonly onContentGenerateStart = this._onContentGenerateStartEmitter.event;

    private _onContentGenerateEmitter = new Emitter<{ issueGroup: ParagraphIssueGroup; content: string }>();
    readonly onContentGenerate = this._onContentGenerateEmitter.event;

    private _onGenerationCompleteEmitter = new Emitter<{ issueGroup: ParagraphIssueGroup; success: boolean; finalContent?: string }>();
    readonly onGenerationComplete = this._onGenerationCompleteEmitter.event;

    constructor(
        @inject(FixContentApi)
        private readonly _fixContentApi: FixContentApi,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
    ) {}

    async generateFixContent(content: string, issueGroup: ParagraphIssueGroup): Promise<boolean> {
        console.log('generateFixContent', issueGroup);

        // 通过事件通知生成状态变化，不直接修改入参
        this._onContentGeneratingEmitter.fire({ issueGroup, isGenerating: true });
        this._onContentGenerateStartEmitter.fire({ issueGroup });

        try {
            const request = this._buildFixContentRequest(content, issueGroup);
            const sseStream = this._fixContentApi.fixContent(request);

            const result = await this._handleSSEStream(sseStream, issueGroup);
            return result.success;
        } catch (error) {
            console.error('Failed to generate replace content:', error);
            this._onContentGeneratingEmitter.fire({ issueGroup, isGenerating: false });
            this._onGenerationCompleteEmitter.fire({ issueGroup, success: false });
            return false;
        }
    }

    private _buildFixContentRequest(content: string, issueGroup: ParagraphIssueGroup): FixContentRequest {
        return {
            chapter_type: issueGroup.chapterId,
            errors: issueGroup.issues
                .filter(issue => !issue.isIgnored)
                .map(issue => issue.id),
            original_content: content,
            task_id: this._generateTaskId(),
        };
    }

    private async _handleSSEStream(sseStream: FixContentResponse, issueGroup: ParagraphIssueGroup): Promise<{ success: boolean; finalContent: string }> {
        let accumulatedContent = '';
        let success = false;

        // 监听 SSE 消息
        sseStream.onMessage((response: any) => {
            const { data } = response;

            if (data?.content) {
                accumulatedContent = data.content;
                this._onContentGenerateEmitter.fire({
                    issueGroup,
                    content: accumulatedContent,
                });
            }

            if (data?.status === StreamStatus.FINISH) {
                success = true;
            }
        });

        // 监听完成事件
        sseStream.onFinish(() => {
            this._onContentGeneratingEmitter.fire({ issueGroup, isGenerating: false });
            this._onGenerationCompleteEmitter.fire({
                issueGroup,
                success,
                finalContent: accumulatedContent,
            });
        });

        await sseStream.start();
        return { success, finalContent: accumulatedContent };
    }


    /**
     * 生成任务ID
     * @private
     */
    private _generateTaskId(): string {
        return this._currentTaskService.taskId;
    }
}
